import { Workout } from "../../libs/workout/types";
import { Exercise as ExerciseFromLib } from "../../libs/exercises/types";
import { getTonnage, hasRepsRange } from "../../libs/exercises/utils";

type MuscleDistEntry = { district: string; percentage: number };

/**
 * Trova la muscleDistribution di un esercizio:
 * 1. Se il workout ha già muscleDistribution → usa quello
 * 2. Altrimenti fa lookup nella lista esercizi (tabella EXERCISES) per ID
 */
const getMuscleDistribution = (
  workoutExerciseId: number | string,
  workoutMd: MuscleDistEntry[] | undefined,
  exercisesLookup: Map<string, ExerciseFromLib>
): MuscleDistEntry[] | undefined => {
  if (workoutMd && workoutMd.length > 0) return workoutMd;
  const fromDb = exercisesLookup.get(String(workoutExerciseId));
  return fromDb?.muscleDistribution;
};

/**
 * Trova la specialCategoryId di un esercizio:
 * 1. Se il workout ha già specialCategoryId → usa quello
 * 2. Altrimenti fa lookup nella lista esercizi per ID
 */
const getSpecialCategoryId = (
  workoutExerciseId: number | string,
  workoutCatId: number | undefined,
  exercisesLookup: Map<string, ExerciseFromLib>
): number | undefined => {
  if (workoutCatId != null) return workoutCatId;
  const fromDb = exercisesLookup.get(String(workoutExerciseId));
  return fromDb?.specialCategoryId;
};

/**
 * Crea una mappa ID → Exercise per lookup veloce (O(1))
 */
export const buildExercisesLookup = (exercises: ExerciseFromLib[]): Map<string, ExerciseFromLib> => {
  const map = new Map<string, ExerciseFromLib>();
  for (const ex of exercises) {
    map.set(String(ex.id), ex);
  }
  return map;
};

/**
 * Calcola il numero di set equivalenti per un distretto muscolare in una settimana.
 */
export const getSetWeekAmountForBodyDistrict = (
  workout: Workout,
  district: string,
  weekIndex: number,
  exercisesLookup?: Map<string, ExerciseFromLib>
): number => {
  let amount = 0;
  const lookup = exercisesLookup || new Map();
  workout.data[weekIndex]?.days?.forEach((day) => {
    day.exercises?.forEach((ex) => {
      const md = getMuscleDistribution(ex.exercise.id, ex.exercise.muscleDistribution, lookup);
      if (md && md.length > 0) {
        const entry = md.find((m) => m.district === district);
        if (entry) {
          amount += ex.exercise.sets.length * (entry.percentage / 100);
        }
      }
    });
  });
  return Math.round(amount);
};

/**
 * Calcola la frequenza settimanale per un distretto muscolare.
 */
export const getWeekFrequencyForBodyDistrict = (
  workout: Workout,
  district: string,
  weekIndex: number,
  exercisesLookup?: Map<string, ExerciseFromLib>
): number => {
  let days = 0;
  const lookup = exercisesLookup || new Map();
  workout.data[weekIndex]?.days?.forEach((day) => {
    const dayHasDistrict = day.exercises?.some((ex) => {
      const md = getMuscleDistribution(ex.exercise.id, ex.exercise.muscleDistribution, lookup);
      return md && md.length > 0 && md.some((m) => m.district === district);
    });
    if (dayHasDistrict) days += 1;
  });
  return days;
};

/**
 * Calcola il numero di set per una categoria speciale in una settimana.
 */
export const getSetWeekAmountForSpecialCategory = (
  workout: Workout,
  categoryId: number,
  categoryName: string,
  weekIndex: number,
  exercisesLookup?: Map<string, ExerciseFromLib>
): number => {
  let amount = 0;
  const lookup = exercisesLookup || new Map();
  workout.data[weekIndex]?.days?.forEach((day) => {
    day.exercises?.forEach((ex) => {
      const catId = getSpecialCategoryId(ex.exercise.id, ex.exercise.specialCategoryId, lookup);
      if (catId === categoryId) {
        amount += ex.exercise.sets.length;
      }
    });
  });
  return amount;
};

/**
 * Calcola la frequenza settimanale per una categoria speciale.
 */
export const getWeekFrequencyForSpecialCategory = (
  workout: Workout,
  categoryId: number,
  categoryName: string,
  weekIndex: number,
  exercisesLookup?: Map<string, ExerciseFromLib>
): number => {
  let days = 0;
  const lookup = exercisesLookup || new Map();
  workout.data[weekIndex]?.days?.forEach((day) => {
    const dayHasCategory = day.exercises?.some((ex) => {
      const catId = getSpecialCategoryId(ex.exercise.id, ex.exercise.specialCategoryId, lookup);
      return catId === categoryId;
    });
    if (dayHasCategory) days += 1;
  });
  return days;
};

/**
 * Trova il distretto primario di un esercizio (quello con la percentuale più alta).
 */
export const getPrimaryDistrict = (
  md: MuscleDistEntry[]
): string | null => {
  if (!md || md.length === 0) return null;
  let best = md[0];
  for (const entry of md) {
    if (entry.percentage > best.percentage) best = entry;
  }
  return best.district;
};

/**
 * Dati tonnellaggio per esercizio raggruppati per distretto.
 * Restituisce per ogni esercizio il nome e un array di tonnellaggio per settimana.
 * Un esercizio appare solo nel suo distretto primario (percentuale più alta).
 */
export type ExerciseTonnageEntry = {
  exerciseName: string;
  exerciseId: string;
  tonnagePerWeek: (number | null)[];
};

/**
 * Controlla se un esercizio ha dati di performance inseriti dall'utente.
 * Richiede che almeno un set abbia il peso e le reps fatte (repsDone, oppure
 * le reps prescritte se fisse e lasciate invariate dall'atleta).
 */
const hasPerformanceData = (sets: import("../../libs/workout/types").ExerciseSetFromServer[]): boolean => {
  if (!sets || sets.length === 0) return false;
  return sets.some(
    (s) =>
      s.weight != null &&
      s.weight !== 0 &&
      ((s.repsDone != null && s.repsDone !== 0) || (s.repsDone == null && !hasRepsRange(s) && !!s.reps))
  );
};

export const getTonnageByDistrictAndExercise = (
  workout: Workout,
  selectedDistricts: string[],
  weeksCount: number,
  exercisesLookup: Map<string, ExerciseFromLib>
): ExerciseTonnageEntry[] => {
  // Prima passata: trova quali esercizi rientrano nei distretti selezionati
  // e traccia per ogni settimana se l'esercizio è presente e se ha dati
  const result = new Map<string, ExerciseTonnageEntry>();
  const exercisePresent = new Map<string, boolean[]>(); // chiave → [true/false per settimana]
  const exerciseHasData = new Map<string, boolean[]>();

  for (let weekIdx = 0; weekIdx < weeksCount; weekIdx++) {
    workout.data[weekIdx]?.days?.forEach((day) => {
      day.exercises?.forEach((ex) => {
        const md = getMuscleDistribution(ex.exercise.id, ex.exercise.muscleDistribution, exercisesLookup);
        if (!md || md.length === 0) return;

        const primaryDistrict = getPrimaryDistrict(md);
        if (!primaryDistrict || !selectedDistricts.includes(primaryDistrict)) return;

        const key = String(ex.exercise.id);
        if (!result.has(key)) {
          result.set(key, {
            exerciseId: key,
            exerciseName: ex.exercise.name,
            tonnagePerWeek: new Array(weeksCount).fill(null),
          });
          exercisePresent.set(key, new Array(weeksCount).fill(false));
          exerciseHasData.set(key, new Array(weeksCount).fill(false));
        }

        exercisePresent.get(key)![weekIdx] = true;

        if (hasPerformanceData(ex.exercise.sets)) {
          exerciseHasData.get(key)![weekIdx] = true;
          const tonnage = getTonnage(ex.exercise.sets);
          const tonnageValue = typeof tonnage === "number" ? tonnage : 0;
          const current = result.get(key)!.tonnagePerWeek[weekIdx];
          result.get(key)!.tonnagePerWeek[weekIdx] = (current ?? 0) + tonnageValue;
        }
      });
    });
  }

  // Per ogni esercizio/settimana: null se non ha dati, numero se li ha (anche 0)
  for (const [key, entry] of result) {
    const hasData = exerciseHasData.get(key)!;
    for (let i = 0; i < weeksCount; i++) {
      if (!hasData[i]) {
        entry.tonnagePerWeek[i] = null;
      }
    }
  }

  return Array.from(result.values());
};

/**
 * Formula di Brzycki per stimare l'1RM:
 * 1RM = peso / (1.0278 - 0.0278 * reps)
 */
const estimateOneRM = (weight: number, reps: number): number => {
  if (reps <= 0 || weight <= 0) return 0;
  if (reps === 1) return weight;
  const denominator = 1.0278 - 0.0278 * reps;
  if (denominator <= 0) return 0;
  return weight / denominator;
};

/**
 * Dati Top Set per esercizio raggruppati per distretto.
 * Per ogni esercizio mostra solo il set marcato come topSet.
 * Il valore è l'1RM stimato con la formula di Brzycki.
 */
export type ExerciseTopSetEntry = {
  exerciseName: string;
  exerciseId: string;
  oneRMPerWeek: (number | null)[];
};

export const getTopSetByDistrictAndExercise = (
  workout: Workout,
  selectedDistricts: string[],
  weeksCount: number,
  exercisesLookup: Map<string, ExerciseFromLib>
): ExerciseTopSetEntry[] => {
  const result = new Map<string, ExerciseTopSetEntry>();

  for (let weekIdx = 0; weekIdx < weeksCount; weekIdx++) {
    workout.data[weekIdx]?.days?.forEach((day) => {
      day.exercises?.forEach((ex) => {
        const md = getMuscleDistribution(ex.exercise.id, ex.exercise.muscleDistribution, exercisesLookup);
        if (!md || md.length === 0) return;

        const primaryDistrict = getPrimaryDistrict(md);
        if (!primaryDistrict || !selectedDistricts.includes(primaryDistrict)) return;

        // Cerca il set marcato come topSet
        const topSet = ex.exercise.sets.find((s) => s.topSet === true);
        if (!topSet) return;

        const key = String(ex.exercise.id);
        if (!result.has(key)) {
          result.set(key, {
            exerciseId: key,
            exerciseName: ex.exercise.name,
            oneRMPerWeek: new Array(weeksCount).fill(null),
          });
        }

        // Calcola 1RM solo se il peso è stato inserito
        const weight = typeof topSet.weight === "number" ? topSet.weight : 0;
        if (weight <= 0) return;

        // Reps effettive: repsDone > repsRange > reps
        let reps = 0;
        if (typeof topSet.repsDone === "number") {
          reps = topSet.repsDone;
        } else if (typeof topSet.repsRange === "number") {
          reps = topSet.repsRange;
        } else if (typeof topSet.reps === "number") {
          reps = topSet.reps;
        }

        if (reps > 0) {
          result.get(key)!.oneRMPerWeek[weekIdx] = estimateOneRM(weight, reps);
        }
      });
    });
  }

  return Array.from(result.values());
};
